<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";

import actions from "~/actions";
import DartboardChip from "~/components/dumb/DartboardChip.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { UNSET_DARTBOARD_LABEL, UNSET_DARTBOARD_PSEUDO_DUID } from "~/constants/dartboard";
import { makeDropdownItem } from "~/constants/dropdown";
import { DROPDOWN_REGISTER_SUBDROPDOWN_STATE } from "~/constants/injection";
import { colorsByTheme } from "~/constants/style";
import { DartboardFieldIcon } from "~/icons";
import { CommandId, EditorMode, Placement } from "~/shared/enums";
import type { DropdownMenuItem, DropdownMenuSection, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { getPageDisplayName } from "~/utils/common";

const props = defineProps<{
  tasks: Task[];
  editorMode: EditorMode;
  value?: string;
  hideUnset?: boolean;
  itemIndex?: number;
  onAfterClose?: (value?: string) => void;
}>();

const dropdownMenuRef = ref<InstanceType<typeof DropdownMenu> | null>(null);

const isOpen = computed(() => dropdownMenuRef.value?.isOpen ?? false);

const registerSubdropdownState = inject(DROPDOWN_REGISTER_SUBDROPDOWN_STATE, null);

watch(isOpen, (newValue) => {
  if (registerSubdropdownState && props.itemIndex !== undefined) {
    registerSubdropdownState(props.itemIndex, newValue);
  }
});

const emit = defineEmits<{
  update: [newDartboardDuid: string];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);

const isChipMode = computed(
  () => props.editorMode === EditorMode.CHIP || props.editorMode === EditorMode.CHIP_RECOMMENDATION
);
const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isFormOrAutomationMode = computed(() => isFormMode.value || props.editorMode === EditorMode.AUTOMATION);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);

// TODO incremental, convert these to useAsyncTasks and await
const selectedTasks = computed(() =>
  !isListMode.value
    ? props.tasks
    : dataStore.getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
        includeTrashed: false,
        includeDraft: false,
      })
);

const selectedDartboardDuid = computed(() => {
  if (isFormOrAutomationMode.value) {
    return props.value ?? UNSET_DARTBOARD_PSEUDO_DUID;
  }

  return props.tasks.every((task) => task.dartboardDuid === props.tasks[0].dartboardDuid)
    ? props.tasks[0].dartboardDuid
    : undefined;
});

const dropdownSections = computed(() => {
  const dartboardDuidInSelectedTasks =
    (props.value ?? selectedTasks.value.every((task) => task.dartboardDuid === selectedTasks.value[0]?.dartboardDuid))
      ? selectedTasks.value[0]?.dartboardDuid
      : undefined;

  const currentDartboard = dartboardDuidInSelectedTasks
    ? dataStore.getDartboardByDuid(dartboardDuidInSelectedTasks)
    : undefined;
  const currentSpaceDuid =
    currentDartboard?.spaceDuid ??
    (appStore.currentPage && "spaceDuid" in appStore.currentPage ? appStore.currentPage.spaceDuid : undefined);

  const sortedSpaces = [...dataStore.spaceList].sort((a, b) => {
    if (a.duid === currentSpaceDuid) {
      return -1;
    }
    if (b.duid === currentSpaceDuid) {
      return 1;
    }
    return 0;
  });

  const makeItem = (isSelected: boolean, item: Omit<DropdownMenuItem, "kind" | "disabled" | "selected">) =>
    makeDropdownItem(isSelected, pageStore.isMobile, item);

  const res: DropdownMenuSection[] = [];
  if (isFormOrAutomationMode.value && !props.hideUnset) {
    res.push({
      title: UNSET_DARTBOARD_LABEL,
      items: [
        makeItem(selectedDartboardDuid.value === UNSET_DARTBOARD_PSEUDO_DUID, {
          title: UNSET_DARTBOARD_LABEL,
          icon: DartboardFieldIcon,
          iconArgs: { class: "icon-md text-vlt" },
          onClick: () => emit("update", UNSET_DARTBOARD_PSEUDO_DUID),
        }),
      ],
    });
  }
  res.push(
    ...sortedSpaces.map((space) => ({
      title: space.title,
      showTitle: true,
      items: dataStore.getDartboardsBySpaceDuidOrdered(space.duid, { excludeFinished: true }).map((dartboard) => {
        const isSelected = isFormOrAutomationMode.value
          ? dartboard.duid === selectedDartboardDuid.value
          : dartboard.duid === dartboardDuidInSelectedTasks;
        return makeItem(isSelected, {
          title: getPageDisplayName(dartboard, dataStore.getSpaceByDuid),
          icon: PageIcon,
          iconArgs: { page: dartboard },
          onClick: async () => {
            emit("update", dartboard.duid);
            if (isFormOrAutomationMode.value) {
              return;
            }

            await actions.visualization.moveTasksToDartboard(
              dartboard.duid,
              selectedTasks.value.length > 0 ? selectedTasks.value : undefined
            );
          },
        });
      }),
    }))
  );
  return res;
});

const onAfterClose = () => {
  props.onAfterClose?.(selectedDartboardDuid.value);
};

defineExpose({
  open: () => dropdownMenuRef.value?.open(),
  isOpen,
});
</script>

<template>
  <DropdownMenu
    ref="dropdownMenuRef"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :sections="dropdownSections"
    :block="isListMode || isFormOrAutomationMode"
    :height-block="isListMode || isFormOrAutomationMode"
    header-title="Set dartboard"
    mobile-include-done
    :skidding="isFormOrAutomationMode ? -1 : undefined"
    :distance="isFormOrAutomationMode ? 2 : undefined"
    :cover="!isContextMenuMode && !isFormOrAutomationMode"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :on-after-close="onAfterClose"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }">
    <DropdownMenuItemContent v-if="isContextMenuMode" :icon="DartboardFieldIcon" title="Change dartboard" is-submenu />

    <Tooltip
      v-else
      class="relative"
      :disabled="isFormOrAutomationMode"
      :command-id="CommandId.MOVE_TO_DARTBOARD"
      :block="isListMode || isFormOrAutomationMode || isTaskDetailMode"
      :height-block="isListMode || isFormOrAutomationMode">
      <div
        v-if="selectedDartboardDuid === UNSET_DARTBOARD_PSEUDO_DUID"
        class="flex h-full items-center text-sm text-vlt"
        :class="isFormOrAutomationMode ? 'ml-2' : 'ml-1.5'">
        Set dartboard
      </div>
      <template v-else>
        <DartboardChip
          :selected-dartboard-duid="selectedDartboardDuid"
          :editor-mode="editorMode"
          :task-duid="isTaskDetailMode && tasks.length === 1 ? tasks[0].duid : undefined" />
      </template>
    </Tooltip>
  </DropdownMenu>
</template>
